import {
  Button,
  Popover,
  Box,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { useState, useEffect } from "react";

function DateFilter({ value, onChange, component }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const dateMenuOpen = Boolean(anchorEl);

  const [selectedDateFilter, setSelectedDateFilter] = useState(null);
  const [selectedDateTitle, setSelectedDateTitle] = useState("Dates");

  const [fromDate, setFromDate] = useState(value?.from || null);
  const [toDate, setToDate] = useState(value?.to || null);

  const handleOpenDateMenu = (e) => setAnchorEl(e.currentTarget);
  const handleCloseDateMenu = () => setAnchorEl(null);

  /* -------------------- CLEAR -------------------- */
  const clearDateFilter = (e) => {
    e.stopPropagation();
    setSelectedDateFilter(null);
    setFromDate(null);
    setToDate(null);
    setSelectedDateTitle("Dates");
    onChange({ dateFrom: null, dateTo: null });
  };

  /* -------------------- APPLY CUSTOM -------------------- */
  const applyCustomDates = () => {
    if (!fromDate || !toDate) return;

    const newFrom = moment(fromDate).format("DD-MM-YYYY");
    const newTo = moment(toDate).format("DD-MM-YYYY");

    // prevent duplicate apply
    if (value?.from === newFrom && value?.to === newTo) {
      handleCloseDateMenu();
      return;
    }

    setSelectedDateTitle(
      `${moment(fromDate).format("DD MMM, YYYY")} - ${moment(toDate).format(
        "DD MMM, YYYY",
      )}`,
    );

    onChange({
      dateFrom: newFrom,
      dateTo: newTo,
    });

    handleCloseDateMenu();
  };

  /* -------------------- QUICK RANGES -------------------- */
  const selectQuickRange = (label, key, start, end) => {
    const newFrom = moment(start).format("DD-MM-YYYY");
    const newTo = moment(end).format("DD-MM-YYYY");

    //  prevent re-selecting same value
    if (value?.from === newFrom && value?.to === newTo) {
      handleCloseDateMenu();
      return;
    }

    setSelectedDateFilter(key);
    setSelectedDateTitle(label);
    setFromDate(start);
    setToDate(end);

    onChange({
      dateFrom: newFrom,
      dateTo: newTo,
    });

    handleCloseDateMenu();
  };

  useEffect(() => {
    if (value?.from && value?.to) {
      const from = moment(value.from, "DD-MM-YYYY");
      const to = moment(value.to, "DD-MM-YYYY");

      setFromDate(from.toDate());
      setToDate(to.toDate());
      setSelectedDateTitle(
        `${from.format("DD MMM, YYYY")} - ${to.format("DD MMM, YYYY")}`,
      );
    } else {
      setSelectedDateFilter(null);
      setSelectedDateTitle("Dates");
      setFromDate(null);
      setToDate(null);
    }
  }, [value]);
  // useEffect(() => {
  //   if (value?.from && value?.to) {
  //     const from = moment(value.from, "DD-MM-YYYY");
  //     const to = moment(value.to, "DD-MM-YYYY");

  //     setFromDate(from.toDate());
  //     setToDate(to.toDate());

  //     const today = moment().startOf("day");
  //     const sevenDaysAgo = moment().subtract(6, "days").startOf("day");

  //     if (from.isSame(sevenDaysAgo, "day") && to.isSame(today, "day")) {
  //       setSelectedDateFilter("last_7_days");
  //       setSelectedDateTitle("Last 7 Days");
  //     } else {
  //       setSelectedDateTitle(
  //         `${from.format("DD MMM, YYYY")} - ${to.format("DD MMM, YYYY")}`
  //       );
  //     }
  //   }
  // }, [value]);
  // useEffect(() => {
  //   if (!value?.from && !value?.to) {
  //     setSelectedDateFilter(null);
  //     setSelectedDateTitle("Dates");
  //     setFromDate(null);
  //     setToDate(null);
  //   }
  // }, []);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpenDateMenu}
        endIcon={
          <Icon
            icon={
              dateMenuOpen
                ? "eva:arrow-ios-upward-fill"
                : "eva:arrow-ios-downward-fill"
            }
            width="18"
            height="18"
          />
        }
        sx={{
          textTransform: "none",
          borderRadius: "8px",
          height: "38px",
          color: "var(--app-text)",
          bgcolor:
            component === "interpretations" || component === "referred-cases"
              ? "var(--app-surface)"
              : "var(--app-surface)",
          px: 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          border: "none",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>{selectedDateTitle}</span>

          {selectedDateTitle !== "Dates" && (
            <Box
              sx={{
                minWidth: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#6C63FF",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              1
            </Box>
          )}
        </span>

        {selectedDateTitle !== "Dates" && (
          <IconButton size="small" onClick={clearDateFilter} sx={{ p: 0 }}>
            <Icon icon="eva:close-fill" width="18" height="18" />
          </IconButton>
        )}
      </Button>

      {/* -------------------- POPOVER -------------------- */}
      <Popover
        open={dateMenuOpen}
        onClose={handleCloseDateMenu}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Quick ranges
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 2,
            }}
          >
            {(() => {
              const today = new Date();
              const startOfToday = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate(),
              );
              const endOfToday = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate(),
                23,
                59,
                59,
                999,
              );

              const presets = [
                ["Today", "today", startOfToday, endOfToday],
                [
                  "Last 7 Days",
                  "last_7_days",
                  new Date(startOfToday.getTime() - 6 * 86400000),
                  endOfToday,
                ],
                [
                  "Last 30 Days",
                  "last_30_days",
                  new Date(startOfToday.getTime() - 29 * 86400000),
                  endOfToday,
                ],
                [
                  "Last 3 Months",
                  "last_3_months",
                  new Date(today.setMonth(today.getMonth() - 3)),
                  endOfToday,
                ],
                [
                  "Last 6 Months",
                  "last_6_months",
                  new Date(today.setMonth(today.getMonth() - 6)),
                  endOfToday,
                ],
              ];

              return presets.map(([label, key, start, end]) => (
                <Button
                  key={key}
                  size="small"
                  onClick={() => selectQuickRange(label, key, start, end)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "9px",
                    border: "1px solid var(--app-border)",
                    height: "36px",
                    color: "var(--app-text)",
                    bgcolor:
                      selectedDateFilter === key ? "var(--app-surface-2)" : "transparent",
                    disabled: selectedDateFilter === key,
                  }}
                >
                  {label}
                </Button>
              ));
            })()}
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Custom range
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={setFromDate}
              maxDate={new Date()}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="To"
              value={toDate}
              onChange={setToDate}
              minDate={fromDate || undefined}
              maxDate={new Date()}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={handleCloseDateMenu}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={applyCustomDates}
              disabled={!fromDate || !toDate}
              sx={{
                backgroundColor: "#5751E9",
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default DateFilter;
